import { useReCaptcha } from "@/hooks/useReCaptcha";
import { UnauthedLayout } from "@/layouts/unauth";
import { useSignInPagesSigninMutation } from "./__gql__/sign-in.gen";
import { type NextPage } from "next";
import { useRouter } from "next/router";
// biome-ignore lint/correctness/noUnusedImports :[AUTOMATICALLY-INSERTED] This import is unused.
import React, { useCallback, useState } from "react";
import ReCaptcha from "react-google-recaptcha";
import { useForm, FormProvider } from "react-hook-form";
import { pagesPath } from "@/pathpida/generated/$path";
import { SigninForms } from "@/pages/signin/__components__/signin-forms";
import { type SigninFormValues } from "./__components__/signin-forms/type";
import { defaultValuesSignInPage } from "./__components__/signin-forms/const";
import { MfaForms } from "@/pages/signin/__components__/mfa-forms";
import { type MfaFormValues } from "./__components__/mfa-forms/type";
import { useApolloError } from "@/hooks/useApolloError";
import { NextSeo } from "next-seo";
import { localStorageKeyRedirectFrom } from "@/configs/localstorage";
import { useSignInPagesAuthorizeMfaCodeMutation } from "./__gql__/authorize-mfa-code.gen";
import { SignInResult } from "@/gql/generated/graphql.gen";
import { type Screen } from "./const";
import { useSignInPagesAuthorizeRecoveryCodeMutation } from "./__gql__/authorize-recovery-code.gen";
import { type RecoveryFormValues } from "./__components__/recovery-forms/type";
import { defaultValuesRecoveryForm } from "./__components__/recovery-forms/const";
import { RecoveryForms } from "@/pages/signin/__components__/recovery-forms";
import { defaultValuesMfaForm } from "./__components__/mfa-forms/const";
import { Box } from "@/components/essentials/box";

const SignInPage: NextPage = () => {
  const router = useRouter();
  const [screen, setScreen] = useState<Screen>("signin");
  // biome-ignore lint/correctness/useExhaustiveDependencies :[AUTOMATICALLY-INSERTED] This hook specifies more dependencies than necessary: setScreen
  const changeScreen = useCallback(
    (screen: Screen) => {
      window.scrollTo(0, 0);
      setScreen(screen);
    },
    [setScreen],
  );
  const { isRecaptchaLoaded, withReCaptcha, reCaptchaProps, PolicySentence } = useReCaptcha();
  const login = useCallback(async () => {
    await router.push(localStorage.getItem(localStorageKeyRedirectFrom) ?? pagesPath.$url());
    localStorage.removeItem(localStorageKeyRedirectFrom);
  }, [router]);
  const [signInPagesSigninMutation, resultSignInPagesSigninMutation] = useSignInPagesSigninMutation();
  useApolloError(resultSignInPagesSigninMutation.error);
  const [authorizeMfaCodeMutation, authorizeMfaCodeMutationResult] = useSignInPagesAuthorizeMfaCodeMutation();
  useApolloError(authorizeMfaCodeMutationResult.error);
  const [authorizeRecoveryCodeMutation, authorizeRecoveryCodeMutationResult] =
    useSignInPagesAuthorizeRecoveryCodeMutation();
  useApolloError(authorizeRecoveryCodeMutationResult.error);

  const signinMethods = useForm<SigninFormValues>({
    defaultValues: defaultValuesSignInPage,
    mode: "all",
  });
  const mfaMethods = useForm<MfaFormValues>({
    defaultValues: defaultValuesMfaForm,
    mode: "all",
  });
  const recoveryMethods = useForm<RecoveryFormValues>({
    defaultValues: defaultValuesRecoveryForm,
    mode: "all",
  });

  const handleSignin = useCallback(
    async ({ email, password, token }: SigninFormValues): Promise<void> => {
      try {
        const { data } = await signInPagesSigninMutation({
          variables: {
            id: email,
            password,
            token,
          },
        });
        if (data?.signIn === SignInResult.Success) {
          // biome-ignore lint/complexity/noVoid :[AUTOMATICALLY-INSERTED] The use of void is not allowed.
          void login();
        } else if (data?.signIn === SignInResult.RequireMfa) {
          changeScreen("mfa");
        }
        // biome-ignore lint/suspicious/noEmptyBlockStatements :[AUTOMATICALLY-INSERTED] Unexpected empty block.
      } catch (e: unknown) {}
    },
    [signInPagesSigninMutation, changeScreen, login],
  );

  const handleAuthorizeMfaCode = useCallback(
    async ({ mfaCode }: MfaFormValues): Promise<void> => {
      try {
        const { data } = await authorizeMfaCodeMutation({
          variables: { mfaCode },
        });
        if (data?.authorizeMfaCode) {
          // biome-ignore lint/complexity/noVoid :[AUTOMATICALLY-INSERTED] The use of void is not allowed.
          void login();
        }
        // biome-ignore lint/suspicious/noEmptyBlockStatements :[AUTOMATICALLY-INSERTED] Unexpected empty block.
      } catch (e: unknown) {}
    },
    [authorizeMfaCodeMutation, login],
  );

  const handleAuthorizeRecoveryCode = useCallback(
    async ({ recoveryCode }: RecoveryFormValues): Promise<void> => {
      try {
        const { data } = await authorizeRecoveryCodeMutation({
          variables: { recoveryCode },
        });
        if (data?.authorizeRecoveryCode) {
          // biome-ignore lint/complexity/noVoid :[AUTOMATICALLY-INSERTED] The use of void is not allowed.
          void login();
        }
        // biome-ignore lint/suspicious/noEmptyBlockStatements :[AUTOMATICALLY-INSERTED] Unexpected empty block.
      } catch (e: unknown) {}
    },
    [authorizeRecoveryCodeMutation, login],
  );

  return (
    <>
      <NextSeo title="サインイン" description="サインインするとToysub! のマイページがご利用できます。" />
      <UnauthedLayout pt="1.5rem" px="1rem" bgColor="light.main">
        {screen === "signin" && (
          <FormProvider {...signinMethods}>
            <form
              onSubmit={(e) => {
                // biome-ignore lint/complexity/noVoid :[AUTOMATICALLY-INSERTED] The use of void is not allowed.
                void signinMethods.handleSubmit(withReCaptcha(handleSignin))(e);
              }}
            >
              <SigninForms isRecaptchaLoaded={isRecaptchaLoaded} loading={resultSignInPagesSigninMutation.loading} />
              <PolicySentence />
              <Box className="captcha" style={{ visibility: "hidden" }}>
                <ReCaptcha {...reCaptchaProps} size="invisible" />
              </Box>
              <ReCaptcha {...reCaptchaProps} size="invisible" />
            </form>
          </FormProvider>
        )}
        {screen === "mfa" && (
          <FormProvider {...mfaMethods}>
            <form
              onSubmit={(e) => {
                // biome-ignore lint/complexity/noVoid :[AUTOMATICALLY-INSERTED] The use of void is not allowed.
                void mfaMethods.handleSubmit(handleAuthorizeMfaCode)(e);
              }}
            >
              <MfaForms
                isLoading={authorizeMfaCodeMutationResult.loading}
                onChangeRecoveryCodeScreen={() => {
                  changeScreen("recovery");
                }}
              />
            </form>
          </FormProvider>
        )}
        {screen === "recovery" && (
          <FormProvider {...recoveryMethods}>
            <form
              onSubmit={(e) => {
                // biome-ignore lint/complexity/noVoid :[AUTOMATICALLY-INSERTED] The use of void is not allowed.
                void recoveryMethods.handleSubmit(handleAuthorizeRecoveryCode)(e);
              }}
            >
              <RecoveryForms
                isLoading={authorizeRecoveryCodeMutationResult.loading}
                onChangeMfaCodeScreen={() => {
                  changeScreen("mfa");
                }}
              />
            </form>
          </FormProvider>
        )}
      </UnauthedLayout>
    </>
  );
};

export default SignInPage;
